import { useMemo } from 'react';
import { useRequestTracker } from '@lingoda/core';
import { useDispatch, useSelector } from '@lingoda/hooks';
import { getAgreementsAgreed } from '@lingoda/purchases';
import { type ChangeSubscriptionPurchase, useChangeSubscription } from '@lingoda/subscriptions';
import { fetchMe } from '@lingoda/auth';
import {
    changeLearningLanguageSuccessPath,
    goTo,
    postCheckoutSuccessLanguageChangePath,
    resolvePath,
    studentChangedCoursePath,
} from '@lingoda/router';
import {
    StudentLearningLanguageDocument,
    UserSubscriptionsDocument,
    useStudentOnboardingAndSubscriptionInfoQuery,
    useUpdateUserLanguageAndSubscriptionMutation,
} from '@lingoda/monolith-graphql';
import { paymentMethodsSelector } from '../selectors';
import { PaymentGateway } from '../models';
import { addPaymentMethod } from '../actions';
import type { PaymentCallbackReturn } from '../hoc/withPayment';
import type { PaymentFormData } from '../hoc';
import type { PaymentOptions } from '../models';

const usePaymentCallbacksForSubscriptionChange = (
    purchase: ChangeSubscriptionPurchase,
    newSectionName?: SectionName,
    newCurriculumId?: number,
) => {
    const dispatch = useDispatch();
    const trackRequest = useRequestTracker();
    const paymentMethods = useSelector(paymentMethodsSelector);
    const [updateUserLanguageAndSubscription] = useUpdateUserLanguageAndSubscriptionMutation({
        refetchQueries: [UserSubscriptionsDocument],
    });
    const { data } = useStudentOnboardingAndSubscriptionInfoQuery();
    const isOnboarded = data?.student.onboarded;
    const hasSubscription = !!data?.studentSubscriptionInfo?.currentSubscription;
    const [changeSubscription] = useChangeSubscription();

    return useMemo(() => {
        const getRedirectUrl = () => {
            if (newSectionName) {
                // Note: change learning language flow
                return hasSubscription && !isOnboarded
                    ? postCheckoutSuccessLanguageChangePath()
                    : changeLearningLanguageSuccessPath();
            }

            return studentChangedCoursePath();
        };

        const submitPayment = async (formData: PaymentFormData, options: PaymentOptions) => {
            const { paymentMethod } = formData;
            const agreementsPayload = getAgreementsAgreed(
                purchase.agreements ?? [],
                formData.agreements,
            );

            if (newSectionName && newCurriculumId) {
                await updateUserLanguageAndSubscription({
                    variables: {
                        purchaseId: purchase.id,
                        agreements: agreementsPayload,
                        newSectionName: newSectionName,
                        newCurriculumId: newCurriculumId,
                        couponCode: purchase.coupons[0]?.code,
                    },
                    refetchQueries: [StudentLearningLanguageDocument, UserSubscriptionsDocument],
                });
            } else {
                await changeSubscription(purchase, agreementsPayload);
            }

            const isExistingPaymentMethod = paymentMethods.some(
                (method) => method.id === paymentMethod && method.stored,
            );

            const redirectUrl = getRedirectUrl();

            if (isExistingPaymentMethod) {
                dispatch(fetchMe());
                goTo(redirectUrl);

                return;
            }

            return trackRequest(
                dispatch(
                    addPaymentMethod({
                        ...options,
                        redirectUrl: resolvePath(redirectUrl),
                        currency: purchase.chargeTotal.currency,
                        paymentMethod,
                        nameOnCard: formData.name,
                    }),
                ),
            ) as Promise<PaymentCallbackReturn>;
        };

        const onSubmitCallback = (formData: PaymentFormData) =>
            submitPayment(formData, { gateway: PaymentGateway.ADYEN });

        const fetchIntentSecret = (formData: PaymentFormData) =>
            submitPayment(formData, { gateway: PaymentGateway.STRIPE });

        return [onSubmitCallback, fetchIntentSecret] as const;
    }, [
        dispatch,
        paymentMethods,
        purchase,
        trackRequest,
        updateUserLanguageAndSubscription,
        newSectionName,
        newCurriculumId,
        hasSubscription,
        isOnboarded,
        changeSubscription,
    ]);
};

export default usePaymentCallbacksForSubscriptionChange;
